import { useState } from 'react';
import { Helmet } from 'react-helmet';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, Divider, Modal, Space } from 'antd';
import { Link } from 'react-router-dom';
import moment from 'moment';

import {
  useDiagnosticRemove,
  useDiagnostics,
} from '@/core/domain/workorder/Diagnostics/Diagnostics.hooks';
import {
  Diagnostic,
  DiagnosticsQuery,
} from '@/core/domain/workorder/Diagnostics/Diagnostics.types';

import { flatInfiniteData } from '@/utils/request/flatInfiniteData';

import { PageProps } from '@/types/PageProps';

import AppPageHeader from '@/components/_common/AppPageHeader';
import InfiniteTable, { ColumnsType } from '@/components/_common/InfiniteTable';
import TabContentWrapper from '@/components/_common/TabContentWrapper';
import DiagnosticsExtraFilters from '@/components/Movements/Diagnostics/DiagnosticsExtraFilters';
import { getTableScrollX } from '@/utils/table';
import EditIcon from '@/components/_common/EditIcon';
import DeleteIcon from '@/components/_common/DeleteIcon';
import { useDebounce } from '@/hooks';
import { convertParams, usePersistSearchParams } from '@/utils/queryParams';

function DiagnosticsPage({ route }: PageProps) {
  const intl = useIntl();

  const [queryParams, setQueryParams] = useState<DiagnosticsQuery>({
    order: 'DESC',
    ...convertParams<DiagnosticsQuery>({
      search: 'string',
      customer: 'string',
      date: 'string',
      licensePlate: 'string',
      order: 'string',
    }),
  });

  const diagnosticRemove = useDiagnosticRemove();

  const queryDebounce = useDebounce(queryParams, 500);
  usePersistSearchParams(queryDebounce);

  const { data, isFetching, hasNextPage, fetchNextPage } =
    useDiagnostics(queryDebounce);

  const diagnostics = flatInfiniteData(data);

  function onRemoveConfirm(diagnostic: Diagnostic) {
    Modal.confirm({
      title: intl.formatMessage({
        id: 'diagnostics.modal.remove.title',
      }),
      content: intl.formatMessage({
        id: 'diagnostics.modal.remove.content',
      }),
      okType: 'danger',
      okText: intl.formatMessage({
        id: 'commons.list.action.button.remove',
      }),
      onOk: () => diagnosticRemove.mutateAsync(diagnostic.id),
    });
  }

  const columns: ColumnsType<Diagnostic> = [
    {
      key: 'customer',
      dataIndex: ['customer', 'name'],
      title: intl.formatMessage({ id: 'diagnostics.list.header.customer' }),
      width: 200,
      ellipsis: true,
    },
    {
      key: 'creation',
      dataIndex: 'createdAt',
      title: intl.formatMessage({ id: 'diagnostics.list.header.creation' }),
      width: 150,
      ellipsis: true,
      render: (text) => moment(text).format('DD/MM/YYYY'),
    },
    {
      key: 'entryDate',
      dataIndex: 'entryDate',
      title: intl.formatMessage({ id: 'diagnostics.list.header.entryDate' }),
      width: 150,
      ellipsis: true,
      render: (text) =>
        text ? moment(text).format('DD/MM/YYYY - HH:MM') : '-',
    },
    {
      key: 'plate',
      dataIndex: ['vehicle', 'licensePlate'],
      title: intl.formatMessage({ id: 'diagnostics.list.header.plate' }),
      width: 150,
      ellipsis: true,
    },
    {
      key: 'action',
      title: intl.formatMessage({ id: 'commons.list.header.action' }),
      width: 100,
      ellipsis: true,
      align: 'right',
      fixed: 'right',
      render: (_text, record) => (
        <Space>
          <EditIcon redirect={`/app/movements/diagnostics/${record.id}`} />

          <Divider type="vertical" />

          <DeleteIcon
            onClick={() => onRemoveConfirm(record)}
            disabled={record.generateWorkOrder}
          />
        </Space>
      ),
    },
  ];

  return (
    <>
      <Helmet
        title={intl.formatMessage({ id: 'layout.menu.movements.diagnostics' })}
      />

      <TabContentWrapper
        title={route.name}
        extra={
          <Button block type="primary">
            <Link to="/app/movements/diagnostics/create">
              <FormattedMessage id="diagnostics.list.newDiagnostics" />
            </Link>
          </Button>
        }
      >
        <AppPageHeader
          onSearch={(value) =>
            setQueryParams((prev) => ({ ...prev, search: value }))
          }
          searchValue={queryParams.search}
          headerActions={({ isShowingFilters }) =>
            isShowingFilters && (
              <Button
                type="primary"
                onClick={() => setQueryParams({ order: 'DESC' })}
              >
                {intl.formatMessage({ id: 'commons.clear.filters' })}
              </Button>
            )
          }
          extraFilters={
            <DiagnosticsExtraFilters
              queryParams={queryParams}
              setQueryParams={setQueryParams}
            />
          }
        />

        <InfiniteTable<Diagnostic>
          loading={isFetching}
          size="small"
          rowKey={(row) => row.id}
          dataSource={diagnostics}
          columns={columns}
          fetchNextPage={fetchNextPage}
          hasNextPage={hasNextPage}
          scroll={{ x: getTableScrollX(columns) }}
        />
      </TabContentWrapper>
    </>
  );
}

export default DiagnosticsPage;
